/**
 * RAP (Review and Publish) Session Models
 * 
 * These interfaces define the structure for the sandboxed "Open-in-RAP" edit flow
 * where users can edit plans in isolation before committing changes atomically.
 * 
 * @module RapSessionModel
 */

/**
 * Main wrapper for the sandboxed session commit
 * Sent to backend when user commits their RAP edit session
 */
export interface RapCommitPayload {
  /** Control metadata to instruct backend logic */
  meta: RapSessionMeta;
  /** Complete plan data snapshot */
  payload: RapPlanData;
}

/**
 * Control metadata to instruct the backend logic
 * Determines how the backend should process the commit
 */
export interface RapSessionMeta {
  /** Action type - always "COMMIT_RAP_SESSION" */
  action: 'COMMIT_RAP_SESSION';
  
  /** 
   * Commit mode:
   * - OVERWRITE: Replace existing plan (archive old → delete → insert new)
   * - CREATE_NEW: Create as brand new plan
   * - SAVE_DRAFT_SESSION: Mid-session persistence (no commit to production)
   */
  mode: 'OVERWRITE' | 'CREATE_NEW' | 'SAVE_DRAFT_SESSION';
  
  /** Original plan ID (used for OVERWRITE and draft tracking) */
  sourcePlanId: string;
  
  /** Unique session identifier for draft persistence */
  sessionId: string;
  
  /** Organization ID (validated against URL path parameter) */
  orgId: string;
  
  /** User ID (validated against URL path parameter) */
  userId: string;

  /** Stable key generated before a confirmed CREATE_NEW request. */
  creationRequestId?: string;

  /** Basic subscription credit number shown in the confirmation. */
  confirmedCreditNumber?: number;
}

/**
 * The full plan snapshot (Header + Children)
 * Contains all data needed to recreate the plan in the database
 */
export interface RapPlanData {
  /** Plan header/core data */
  planCore: Partial<TaxPlanCore>;
  
  /** List of strategy lines */
  strategies: TaxPlanLine[];
  
  /** List of tasks linked to strategies */
  tasks: RapTask[];
  
  /** Custom tax rates specific to this plan */
  customRates: CustomTaxRate[];
  
  /** ROI and fee calculation data */
  roiFee: RapRoiFeeData;
  
  /** 
   * Re-queried OCR JSON from RAP session (contains modified values)
   * Only present if performed AI re-query during RAP edit
   * Stored as JSON string (similar to how OCR data is stored in OCRRequest.ocrResponse)
   */
  ocrData?: string;
  
  /** 
   * AI suggestions generated during re-query
   * Only present if performed AI re-query during RAP edit
   * Stored as JSON string (array of strategy suggestions)
   */
  ocrSuggestions?: string;
  
  /**
   * Chat ID from re-query (links to AI conversation state)
   * Only present if performed AI re-query during RAP edit
   * Note: chatId is stored in json_payload, not in ocr_requests during session
   * On commit, chatId is set to NULL (mimics normal flow where chat_id becomes NULL after questionnaire)
   */
  chatId?: string | number;

  /**
   * Client milestone data (first/last name, email, phone, enableClientPortal, relatedParties).
   * When present, backend updates the client record on commit.
   */
  client?: any;
}

/**
 * Plan header matching client_tax_rates table
 * Core plan information
 */
export interface TaxPlanCore {
  /** Plan ID - optional (omitted during sandbox editing, set by backend on commit) */
  id?: string;
  
  /** Plan name/title */
  name: string;
  
  /** Tax year for this plan */
  taxYear: number;
  
  /** Client's home state */
  homeState: string;
  
  /** Total tax amount */
  totalTax: number;

  /**
   * True once a user typed Total Tax by hand, so it is never replaced by a
   * bracket estimate. Carried through the sandbox commit so Open-in-RAP keeps
   * the provenance the plan already had.
   */
  totalTaxOverridden?: boolean;

  /** Taxable income amount */
  taxableIncome: number;
  
  /** Marginal tax rate */
  marginalRate: number;
  
  /** Effective tax rate */
  effectiveRate: number;
  
  /** Capital gains rate. Same key as the plan form and the backend (getCapGainRate). */
  capGainRate: number;
  
  /** C-Corporation flat rate */
  cCorpFlatRate: number;
  
  /** Market growth rate */
  marketGrowthRate: number;
  
  /** Plan description */
  description: string;
  
  /** Plan disclaimer text */
  disclaimer: string;
  
  /** Client ID this plan belongs to */
  clientId: string;
  
  /** User ID who created/owns the plan */
  userId: string;
  
  /** Plan status (e.g., 'draft', 'published') */
  status?: string;
  
  /** Individual state tax rate */
  individualStateRate?: number;
  
  /** Corporate state tax rate */
  corporateStateRate?: number;
  
  /** Year 1 total savings from all strategies */
  year1Savings?: number;
  
  /** Year 2 total savings from all strategies */
  year2Savings?: number;
  
  /** 
   * Document ID linking to the uploaded 1040 PDF
   * Used for PDF access and baseline OCR lookup
   */
  documentId?: string;
  
  /** 
   * OCR Request ID - direct link to specific OCR entry in ocr_requests table
   * Priority 1 for OCR lookup (3-tier hierarchy: ocr_request_id > plan_id > file_id)
   * NULL means fallback to document_id baseline lookup
   */
  ocrRequestId?: string;
  
  /** 
   * Source Plan ID - tracks plan lineage (which plan this was created from)
   * NULL for original plans created from PDF
   * Set when plan is created via RAP CREATE_NEW mode
   */
  sourcePlanId?: string;
}

/**
 * Strategy line interface
 * Represents a single tax strategy within a plan
 */
export interface TaxPlanLine {
  /** 
   * Strategy ID 
   * - In sandbox: Session key (e.g., "TMP_STRAT_1")
   * - After commit: Real UUID from database
   */
  id?: string;
  
  /** Strategy type ID (references tax_strategy_planning table) */
  strategyId: string;
  
  /** Strategy name (optional, for display) */
  strategyName?: string;
  
  /** Dollar amount for this strategy */
  amount: number;
  
  /** Actual amount realized */
  actualAmount?: number;
  
  /** ROI amount (federal) */
  roiAmount?: number;
  
  /** State-level ROI amount */
  stateRoiAmount?: number;
  
  /** Tax rate this strategy is associated with */
  associatedWith?: string;
  
  /** State tax rate association */
  stateAssociatedWith?: string;
  
  /** Strategy notes */
  notes?: string;
  
  /** Client/entity ID */
  clientId?: string;
  
  /** Target completion date */
  goalDate?: string;
  
  /** Actual execution date */
  executionDate?: string;
  
  /** Entity ID for multi-entity clients */
  entityId?: string;
  
  /** Whether this strategy recurs yearly */
  recurring: boolean;
  
  /** First year this strategy applies */
  sinceYear?: number;
  
  /** Last year this strategy applies */
  toYear?: number;
  
  /** Strategy type/category */
  type?: string;
  
  /** Type identifier */
  typeId?: string;
  
  /** Strategy description */
  strategyDescription?: string;
  
  /** Actual tax savings realized */
  actualTaxSavingsAmount?: number;
  
  /** Whether this is tax-free income */
  isTaxFreeIncome?: boolean;
  
  /** Display order for sorting */
  displayOrder?: number;
}

/**
 * Task interface
 * Represents an action item linked to a strategy
 */
export interface RapTask {
  /** Task ID (optional, generated by backend) */
  id?: string;
  
  /** Task title */
  title: string;
  
  /** Task description */
  description: string;
  
  /** Due date (ISO string) */
  dueDate: string;
  
  /** Task status (e.g., 'pending', 'completed') */
  status: string;
  
  /** Task priority (e.g., 'high', 'medium', 'low') */
  priority: string;
  
  /** Client ID this task belongs to */
  clientId?: string;
  
  /**
   * Strategy ID this task is linked to
   * - In sandbox: Session key (e.g., "TMP_STRAT_1")
   * - Backend resolves this to real UUID before saving
   */
  strategyId?: string;
  
  /** 
   * Plan line ID (resolved by backend)
   * Maps to actual strategy UUID in database
   */
  planLineId?: string;
  
  /**
   * Related party name (e.g., "Jane Doe (Spouse)", "ABC Corp")
   * Inherited from parent strategy or preserved from source plan.
   * This is a STRING, not an object.
   */
  relatedParty?: string;
  
  /**
   * Task assignees (array of user/staff objects assigned to this task)
   * Preserved from source plan during RAP edit to maintain assignments.
   * Format: Array of objects with {id, name, email, etc.}
   */
  taskAssignees?: any[];
  
  /**
   * Firm/Organization ID for multi-tenant filtering
   * CRITICAL: Required for task visibility in firm-specific task dashboards.
   * Without this, tasks may not appear in filtered views.
   */
  firmId?: string;
}

/**
 * Custom tax rate interface
 * Client-specific tax rates that override defaults
 */
export interface CustomTaxRate {
  /** Rate ID (optional) */
  id?: string;
  
  /** Rate name/label */
  name: string;
  
  /** Tax rate value (as percentage, e.g., 25 for 25%, or decimal 0.25) 
   * NOTE: Backend uses 'rate', UI binds to 'rate', database stores as 'value'
   * This matches the backend CustomTaxRates.java model
   */
  rate: number;
  
  /** 
   * Formula for calculating this rate (JSON string)
   * Allows complex rate calculations
   */
  formula?: string;
  
  /** Organization ID */
  orgId?: string;
  
  /** Client tax plan ID this rate belongs to */
  clientTaxId?: string;
}

/**
 * Fee line item interface
 * Represents a single fee/cost line item in a tax plan
 * Corresponds to the CostLine model in the backend
 */
export interface FeeLineItem {
  /** Fee line ID (optional, generated by backend) */
  id?: string;
  
  /** Fee line name/label */
  name?: string;
  
  /** User ID who created this fee line */
  userId?: string;
  
  /** Client strategy/plan ID this fee belongs to */
  clientStrategyId?: string;
  
  /** Fee amount in dollars */
  amount: number;
  
  /** Fee description */
  description?: string;
  
  /** Year this fee applies to */
  year: number;
  
  /** Whether this fee recurs yearly */
  recurring: boolean;
  
  /** Frequency of the fee (e.g., "Monthly", "Annually", "One-time") */
  frequency?: string;
  
  /** 
   * Fee type 
   * Common values: "Recurring", "Implementation", "One-time"
   */
  type?: string;
  
  /** Tax rate this fee is associated with */
  associatedWith?: string;
  
  /** Start month (for date range fees) */
  fromMonth?: string;
  
  /** End month (for date range fees) */
  toMonth?: string;
  
  /** Platform/source of the fee */
  platform?: string;
  
  /** URL to the proposal document */
  proposalUrl?: string;
  
  /** Implementation calculation data (JSON string) */
  implementationCalc?: string;
}

/**
 * ROI fee data interface
 * Contains fee calculation and ROI projection data
 */
export interface RapRoiFeeData {

  feeLineData: any;
  
  /** * Proposed Year 1 value (Savings)
   * This represents the total savings generated by the strategies
   */
  proposedY1Value: number;


  /** 
   * Implementation calculation data
   * Changed from string to any[] to match Jackson's List<Object> handling
   * IMPORTANT: Must use proposedY1ValueImplCalc to match backend @JsonProperty annotation
   */
  proposedY1ValueImplCalc: any[];
}

/**
 * Type guard to check if a payload is for draft save vs commit
 */
export function isDraftSaveMode(payload: RapCommitPayload): boolean {
  return payload.meta.mode === 'SAVE_DRAFT_SESSION';
}

/**
 * Type guard to check if a payload is for overwrite mode
 */
export function isOverwriteMode(payload: RapCommitPayload): boolean {
  return payload.meta.mode === 'OVERWRITE';
}

/**
 * Type guard to check if a payload is for create new mode
 */
export function isCreateNewMode(payload: RapCommitPayload): boolean {
  return payload.meta.mode === 'CREATE_NEW';
}

/**
 * Helper to generate a unique session ID
 */
export function generateSessionId(): string {
  return `RAP_SESSION_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Helper to generate temporary strategy IDs for sandbox
 */
export function generateTempStrategyId(index: number): string {
  return `TMP_STRAT_${index + 1}`;
}
